import { Link } from 'react-router-dom';
import { blogPath } from '../../utils/slug';
import { imageFallback } from '../../utils/env';
import { formatDate, isoDate, postExcerpt, publishedAt, readingMinutes } from '../../utils/post';

/**
 * Article card.
 *
 * <Link> renders a real <a href>, so crawlers have something to follow and
 * middle-click / ctrl-click open a new tab natively. The category is a plain
 * <span>, not a link — a link nested inside a link is invalid HTML and
 * confuses both browsers and screen readers.
 *
 * @param {object} blog
 * @param {'default'|'featured'|'compact'} variant
 * @param {boolean} priority  set on the above-the-fold cover so it is fetched
 *                            eagerly at high priority (helps LCP)
 * @param {'h2'|'h3'} headingLevel  keeps the document outline correct for the
 *                            section the card sits in
 */
function BlogCard({ blog, variant = 'default', priority = false, headingLevel = 'h3' }) {
  if (!blog) return null;

  const Heading = headingLevel;
  const href = blogPath(blog);
  const date = publishedAt(blog);
  const minutes = readingMinutes(blog);

  return (
    <article className={`blog-card blog-card-${variant}`}>
      <Link className="blog-card-link" to={href}>
        <div className="blog-card-media">
          <img
            src={blog.image || '/default-blog.svg'}
            alt=""
            width="1200"
            height="675"
            loading={priority ? 'eager' : 'lazy'}
            // fetchpriority is lowercase in the DOM; React 18 passes unknown
            // lowercase attributes straight through.
            fetchpriority={priority ? 'high' : undefined}
            decoding="async"
            onError={imageFallback('/default-blog.svg')}
          />
        </div>

        <div className="blog-card-body">
          {blog.category && <span className="chip chip-sm">{blog.category}</span>}

          <Heading className="blog-card-title">{blog.title}</Heading>

          {variant !== 'compact' && (
            <p className="blog-card-excerpt">{postExcerpt(blog, variant === 'featured' ? 220 : 140)}</p>
          )}

          <p className="blog-card-meta">
            <span className="blog-card-author">{blog.author}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={isoDate(date)}>{formatDate(date)}</time>
            <span aria-hidden="true">·</span>
            <span>{minutes} min read</span>
          </p>
        </div>
      </Link>
    </article>
  );
}

export default BlogCard;
